import { Badge, Box, Tooltip } from "@mui/material";
import { Image as ImageIcon } from "@mui/icons-material";
import React, { useState, useEffect, useRef } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { TableCell } from "@/components/molecules/Table/styled";
import { IProductImage } from "@/interfaces/product-image.interface";
import { ImageData } from "@/interfaces/product.interface";
import { ISku } from "@/interfaces/sku.interface";

interface Props {
  sku: ISku;
  productImages: IProductImage[] | ImageData[] | null;
}

export function SkuImageCell({ sku, productImages }: Props) {
  const [isLoading, setIsLoading] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const cellRef = useRef<HTMLTableCellElement>(null);

  // Lazy loading: detect when cell enters viewport
  useEffect(() => {
    if (!cellRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: "50px" }
    );

    observer.observe(cellRef.current);

    return () => observer.disconnect();
  }, []);

  // Determine which image to display
  const getImageToDisplay = (): {
    url: string;
    name: string;
    isSpecific: boolean;
  } => {
    const images = productImages || [];

    // 1. Check if SKU has specific image
    if (sku.imagePhisicalAddress && sku.imagePhisicalAddress !== "") {
      const specificImage = images.find((img) => img.phisicalAddress === sku.imagePhisicalAddress);

      if (specificImage) {
        // Ordem correta: url > preview > publicUrl (url já vem transformada pelo getPublicImageUrl)
        const url =
          specificImage.url ||
          (specificImage as any).preview ||
          (specificImage as any).publicUrl ||
          "";
        if (url) {
          return {
            url,
            name: specificImage.name || "Imagem específica",
            isSpecific: true,
          };
        }
      }
    }

    // 2. Fallback to main product image
    const mainImage = images.find((img) => img.main);
    if (mainImage) {
      const url = mainImage.url || (mainImage as any).preview || (mainImage as any).publicUrl || "";
      if (url) {
        return {
          url,
          name: mainImage.name || "Imagem principal",
          isSpecific: false,
        };
      }
    }

    // 3. Fallback to first product image
    const firstImage = images[0];
    if (firstImage) {
      const url =
        firstImage.url || (firstImage as any).preview || (firstImage as any).publicUrl || "";
      if (url) {
        return {
          url,
          name: firstImage.name || "Primeira imagem",
          isSpecific: false,
        };
      }
    }

    // 4. Fallback to placeholder
    return {
      url: "/placeholder.svg",
      name: "Sem imagem",
      isSpecific: false,
    };
  };

  const imageData = getImageToDisplay();

  const tooltipTitle = imageData.isSpecific
    ? `${imageData.name} (Imagem específica)`
    : `${imageData.name} (Imagem padrão)`;

  return (
    <TableCell ref={cellRef}>
      {!isInView ? (
        <Skeleton className="w-12 h-12 rounded" />
      ) : (
        <Tooltip title={tooltipTitle} arrow>
          <Box
            sx={{
              position: "relative",
              width: 48,
              height: 48,
              display: "inline-block",
            }}
          >
            {isLoading && (
              <Skeleton
                className="w-12 h-12 rounded"
                style={{ position: "absolute", top: 0, left: 0 }}
              />
            )}
            <Box
              component="img"
              src={imageData.url}
              alt={imageData.name}
              onLoad={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
              sx={{
                width: 48,
                height: 48,
                objectFit: "cover",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                display: isLoading ? "none" : "block",
              }}
            />
            {imageData.isSpecific && !isLoading && (
              <Badge
                badgeContent={<ImageIcon sx={{ fontSize: 10 }} />}
                color="primary"
                sx={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  "& .MuiBadge-badge": {
                    fontSize: "0.6rem",
                    height: 16,
                    minWidth: 16,
                    padding: "0 4px",
                  },
                }}
              />
            )}
          </Box>
        </Tooltip>
      )}
    </TableCell>
  );
}
